import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { EvalService } from "@/gen/omnios/v1/eval_connect.js";
import type { EvalMetrics } from "@/gen/omnios/v1/eval_pb.js";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ArrowRight,
	CheckCircle2,
	TrendingDown,
	TrendingUp,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	PolarAngleAxis,
	PolarGrid,
	Radar,
	RadarChart,
	ResponsiveContainer,
	Tooltip,
} from "recharts";

export const Route = createFileRoute("/admin/eval/compare")({
	component: CompareRuns,
});

const evalClient = createClient(EvalService, transport);
const adminClient = createClient(AdminService, transport);

const METRIC_KEYS: Array<{
	key: keyof EvalMetrics;
	label: string;
	pct: boolean;
}> = [
	{ key: "hitAt1", label: "Hit@1", pct: true },
	{ key: "hitAt3", label: "Hit@3", pct: true },
	{ key: "hitAt5", label: "Hit@5", pct: true },
	{ key: "hitAt10", label: "Hit@10", pct: true },
	{ key: "mrr", label: "MRR", pct: false },
	{ key: "ndcgAt5", label: "NDCG@5", pct: false },
	{ key: "ndcgAt10", label: "NDCG@10", pct: false },
	{ key: "unitConversionAccuracy", label: "Unit Acc", pct: true },
];

function CompareRuns() {
	const { t } = useTranslation("eval");
	const [baseId, setBaseId] = useState("");
	const [compareId, setCompareId] = useState("");

	const { data: runsData } = useQuery({
		queryKey: ["admin", "eval-runs-all"],
		queryFn: () =>
			adminClient.listAllEvalRuns({ pageSize: 100, statusFilter: "completed" }),
	});

	const compareMut = useMutation({
		mutationFn: () =>
			evalClient.compareEvalRuns({
				baseRunId: baseId,
				compareRunId: compareId,
			}),
	});

	const comparison = compareMut.data;
	const runs = runsData?.runs ?? [];

	return (
		<div className="space-y-6">
			<h2 className="text-lg font-semibold">{t("compare_title")}</h2>

			<div className="flex items-center gap-4">
				<div className="flex-1">
					<Label className="mb-1 block text-muted-foreground">
						{t("compare_base")}
					</Label>
					<Select value={baseId} onValueChange={setBaseId}>
						<SelectTrigger>
							<SelectValue placeholder={t("compare_select_placeholder")} />
						</SelectTrigger>
						<SelectContent>
							{runs.map((r) => (
								<SelectItem key={r.id} value={r.id}>
									{r.datasetName || r.datasetId.slice(0, 8)} ·{" "}
									{r.algorithmVersion || "—"}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<ArrowRight className="mt-5 h-5 w-5 text-muted-foreground" />
				<div className="flex-1">
					<Label className="mb-1 block text-muted-foreground">
						{t("compare_target")}
					</Label>
					<Select value={compareId} onValueChange={setCompareId}>
						<SelectTrigger>
							<SelectValue placeholder={t("compare_select_placeholder")} />
						</SelectTrigger>
						<SelectContent>
							{runs.map((r) => (
								<SelectItem key={r.id} value={r.id}>
									{r.datasetName || r.datasetId.slice(0, 8)} ·{" "}
									{r.algorithmVersion || "—"}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<Button
					className="mt-5"
					onClick={() => compareMut.mutate()}
					disabled={!baseId || !compareId || compareMut.isPending}
				>
					{t("compare_submit")}
				</Button>
			</div>

			{compareMut.isPending && <Skeleton className="h-64" />}

			{comparison && (
				<div className="space-y-6">
					{comparison.baseMetrics && comparison.compareMetrics && (
						<>
							{/* Metrics grid */}
							<div className="grid gap-3 md:grid-cols-4 lg:grid-cols-8">
								{METRIC_KEYS.map(({ key, label, pct }) => (
									<MetricDiff
										key={key}
										label={label}
										base={comparison.baseMetrics![key] as number}
										compare={comparison.compareMetrics![key] as number}
										pct={pct}
									/>
								))}
							</div>

							{/* Radar chart */}
							<Card>
								<CardHeader>
									<CardTitle className="text-base">
										{t("compare_radar_title")}
									</CardTitle>
								</CardHeader>
								<CardContent>
									<ResponsiveContainer width="100%" height={300}>
										<RadarChart
											data={METRIC_KEYS.map(({ key, label }) => ({
												metric: label,
												base: (comparison.baseMetrics![key] as number) * 100,
												target:
													(comparison.compareMetrics![key] as number) * 100,
											}))}
										>
											<PolarGrid />
											<PolarAngleAxis
												dataKey="metric"
												tick={{ fontSize: 11 }}
											/>
											<Radar
												name={t("compare_base")}
												dataKey="base"
												stroke="hsl(217 91% 60%)"
												fill="hsl(217 91% 60%)"
												fillOpacity={0.1}
												strokeWidth={2}
											/>
											<Radar
												name={t("compare_target")}
												dataKey="target"
												stroke="hsl(142 71% 45%)"
												fill="hsl(142 71% 45%)"
												fillOpacity={0.1}
												strokeWidth={2}
											/>
											<Tooltip
												contentStyle={{
													fontSize: 12,
													borderRadius: 8,
													border: "1px solid hsl(var(--border))",
													backgroundColor: "hsl(var(--background))",
												}}
												formatter={(v: any) => `${Number(v).toFixed(1)}%`}
											/>
										</RadarChart>
									</ResponsiveContainer>
								</CardContent>
							</Card>
						</>
					)}

					{comparison.regressions.length > 0 && (
						<Card className="border-red-200 dark:border-red-900">
							<CardHeader>
								<CardTitle className="flex items-center gap-2 text-base text-red-700 dark:text-red-400">
									<TrendingDown className="h-4 w-4" />
									{t("compare_regressions", {
										count: comparison.regressions.length,
									})}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<DiffTable rows={comparison.regressions} />
							</CardContent>
						</Card>
					)}

					{comparison.improvements.length > 0 && (
						<Card className="border-green-200 dark:border-green-900">
							<CardHeader>
								<CardTitle className="flex items-center gap-2 text-base text-green-700 dark:text-green-400">
									<TrendingUp className="h-4 w-4" />
									{t("compare_improvements", {
										count: comparison.improvements.length,
									})}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<DiffTable rows={comparison.improvements} />
							</CardContent>
						</Card>
					)}

					{comparison.regressions.length === 0 &&
						comparison.improvements.length === 0 && (
							<Card>
								<CardContent className="py-8 text-center text-muted-foreground">
									{t("compare_no_changes")}
								</CardContent>
							</Card>
						)}
				</div>
			)}
		</div>
	);
}

function MetricDiff({
	label,
	base,
	compare,
	pct,
}: { label: string; base: number; compare: number; pct: boolean }) {
	const { t } = useTranslation("eval");
	const diff = compare - base;
	const color =
		Math.abs(diff) < 0.001
			? "text-muted-foreground"
			: diff > 0
				? "text-green-600"
				: "text-red-600";
	const fmt = (v: number) => (pct ? `${(v * 100).toFixed(1)}%` : v.toFixed(3));

	return (
		<Card>
			<CardContent className="pt-4 pb-3">
				<div className="text-xs text-muted-foreground">{label}</div>
				<div className="mt-1 flex items-baseline gap-2">
					<span className="text-xl font-bold">{fmt(compare)}</span>
					<span className={`text-sm font-medium ${color}`}>
						{diff > 0 ? "+" : ""}
						{pct ? `${(diff * 100).toFixed(1)}%` : diff.toFixed(3)}
					</span>
				</div>
				<div className="text-xs text-muted-foreground">
					{t("compare_was", { value: fmt(base) })}
				</div>
			</CardContent>
		</Card>
	);
}

function DiffTable({
	rows,
}: {
	rows: Array<{
		rawText: string;
		baseRow?: {
			actualProductTitle: string;
			matchConfidence: number;
			isCorrect?: boolean;
		};
		compareRow?: {
			actualProductTitle: string;
			matchConfidence: number;
			isCorrect?: boolean;
		};
	}>;
}) {
	const { t } = useTranslation("eval");
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>{t("compare_col_query")}</TableHead>
					<TableHead>{t("compare_col_before")}</TableHead>
					<TableHead>{t("compare_col_after")}</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((r, i) => (
					<TableRow key={i}>
						<TableCell className="max-w-xs truncate font-mono text-xs">
							{r.rawText}
						</TableCell>
						<TableCell className="text-sm">
							<span className="flex items-center gap-1">
								{r.baseRow?.isCorrect ? (
									<CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
								) : (
									<XCircle className="h-3.5 w-3.5 text-red-500" />
								)}
								{r.baseRow?.actualProductTitle || "—"}
								<span className="ml-1 text-xs text-muted-foreground">
									({((r.baseRow?.matchConfidence ?? 0) * 100).toFixed(0)}%)
								</span>
							</span>
						</TableCell>
						<TableCell className="text-sm">
							<span className="flex items-center gap-1">
								{r.compareRow?.isCorrect ? (
									<CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
								) : (
									<XCircle className="h-3.5 w-3.5 text-red-500" />
								)}
								{r.compareRow?.actualProductTitle || "—"}
								<span className="ml-1 text-xs text-muted-foreground">
									({((r.compareRow?.matchConfidence ?? 0) * 100).toFixed(0)}%)
								</span>
							</span>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}
